import { axiosInstance } from '@/rest/axios'

const toCamelCase = (key) =>
  key.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase())

// Mirrors v2-dashboard's `camelize` (src/utils/modelUtils.ts): recursively
// converts snake_case keys to camelCase, including nested objects/arrays.
const camelize = (input) => {
  if (Array.isArray(input)) return input.map(camelize)
  if (input && typeof input === 'object' && input.constructor === Object) {
    return Object.keys(input).reduce((acc, key) => {
      acc[toCamelCase(key)] = camelize(input[key])
      return acc
    }, {})
  }
  return input
}

// The v2-dashboard BFF's own URLs are not the backend's: its relationship route
// rewrites to `/ach/{relationshipId}`; we call the backend's DELETE
// /v2/ach/{relationship_id} directly.
const createAchApi = () => {
  return {
    getAchRelationship: async (account_number) =>
      camelize(await axiosInstance.get(`v2/ach/${account_number}`)),

    getIraReasons: async (account_number, type) =>
      (
        await axiosInstance.get(`v2/ach/${account_number}/reasons`, {
          params: { type },
        })
      ).reasons,

    getAchRelationshipHistory: async (account_number) =>
      camelize(await axiosInstance.get(`v2/ach/${account_number}/history`)),

    getAchReviewQueue: async (params) =>
      camelize(await axiosInstance.get('v2/ach/review', { params })),

    reviewAchRelationship: (relationship_id, body) =>
      axiosInstance.post(`v2/ach/${relationship_id}/review`, body),

    /**
     * @param {string} relationship_id - The relationship record's own `id`,
     *   not `achRelationshipStatic.relationshipId` (the broker-side id).
     */
    deleteAchRelationship: (relationship_id) =>
      axiosInstance.delete(`v2/ach/${relationship_id}`),

    /**
     * Fetch the ACH amount available to withdraw for an account.
     * This is the authoritative value (it nets out any held commission) and
     * should be preferred over the balance table's available_to_withdraw column.
     * @param {string} account_number - Account number.
     * @returns {Promise<AchAvailableToWithdrawData>} Available to withdraw data for provided account number.
     */
    available_to_withdraw: (account_number) =>
      axiosInstance.get(`v2/ach/${account_number}/available-to-withdraw`),

    /**
     * @param {{ start_time?: string, end_time?: string }} [params] - Optional date range.
     * @returns {Promise<AchTransfer[]>} Camelized transfers.
     */
    getTransfers: async (account_number, params) =>
      camelize(
        await axiosInstance.get(`v2/ach/transfers/${account_number}`, {
          params,
        })
      ),

    /**
     * @param {string} transfer_id - The transfer record's own `id`. `transferId`
     *   is null until the broker assigns one.
     */
    cancelTransfer: (transfer_id) =>
      axiosInstance.put(`v2/ach/transfers/${transfer_id}/cancel`),

    /**
     * Top-level keys are camelCase; `iraContributionDistribution`'s are snake_case.
     * @param {AchTransferCreateRequest} body - `relationshipId` is the
     *   relationship record's own `id` (v2-dashboard `useAchTransfer`).
     */
    createDeposit: (account_number, body) =>
      axiosInstance.post(`v2/ach/transfers/${account_number}/deposit`, body),

    /**
     * Same shape as {@link createDeposit}.
     * @param {AchTransferCreateRequest} body
     */
    createWithdrawal: (account_number, body) =>
      axiosInstance.post(`v2/ach/transfers/${account_number}/withdrawal`, body),
  }
}

const achApi = createAchApi()

export { achApi, camelize }

/**
 * @typedef {Object} AchAvailableToWithdrawData
 * @property {string} account_number - Account number.
 * @property {string} available_to_withdraw - Net amount withdrawable via ACH.
 * @property {string} commission_held - Amount held back for commission.
 * @property {string} deposit_hold - Amount held from recent deposits.
 * @property {string} gross_available - Available to withdraw before any holdback.
 * @property {boolean} has_open_orders
 * @property {boolean} has_intraday_orders
 */

/**
 * @typedef {Object} AchTransfer
 * @property {string} accountNumber
 * @property {string} id - Required to cancel.
 * @property {string|null} transferId - Broker id, null until assigned.
 * @property {string} time - ISO timestamp.
 * @property {string} amount
 * @property {string} status
 * @property {string|null} friendlyStatus - Display status, includes direction.
 * @property {'Deposit'|'Withdrawal'|null} transferType
 * @property {string|null} reason
 * @property {string|null} additionalInfo
 * @property {string|null} memo
 * @property {string|null} message
 * @property {string|null} cancel - Cancellation timestamp.
 * @property {'cancel'} action
 */

/**
 * @typedef {Object} AchTransferCreateRequest
 * @property {string} [accountNumber]
 * @property {number} amount
 * @property {string} relationshipId
 * @property {string} [memo]
 * @property {IraContributionDistributionRequest} [iraContributionDistribution] - IRA only.
 * @property {boolean} [brokerOverride]
 */

/**
 * @typedef {Object} IraContributionDistributionRequest
 * @property {string} [contribution_type] - IraContributionType, deposits only.
 * @property {boolean} [apply_to_prior_year] - Deposits only.
 * @property {string} [distribution_reason] - IraDistributionReason, withdrawals only.
 * @property {{ value: number|string, value_type?: 'PERCENTAGE' }} [federal_tax_withholding] - Withdrawals only.
 * @property {{ value: number|string, value_type?: 'PERCENTAGE' }} [state_tax_withholding] - Withdrawals only.
 */
